import React, { useState } from "react"
import Color from "color"
import { normalizeHex } from "./ColorPalette"

interface IProps {
    /** Colour the pad opens on. */
    initial?: string
    onCancel: () => void
    onUse: (hex: string) => void
}

interface HSV {
    h: number
    s: number
    v: number
}

const toHex = (c: HSV) => Color({ h: c.h, s: c.s, v: c.v }).hex().toUpperCase()

const fromHex = (hex?: string): HSV => {
    const safe = normalizeHex(hex) ?? "#3B82F6"
    const o = Color(safe).hsv().object()
    return { h: o.h ?? 0, s: o.s ?? 0, v: o.v ?? 0 }
}

/**
 * A small HSV colour pad: saturation/value square, hue slider, hex field, and an explicit
 * Cancel / Use color pair.
 *
 * Deliberately hand-rolled instead of <input type="color">: the browser's own picker has no
 * confirm button and commits whatever is selected when it's dismissed, so there is no way to
 * back out of it — and no way to tell "I changed my mind" apart from "I chose this".
 */
export default function ColorPad(props: IProps) {

    const [hsv, hsvSet] = useState<HSV>(() => fromHex(props.initial))
    const [text, textSet] = useState<string>(() => toHex(fromHex(props.initial)))

    const hex = toHex(hsv)

    const update = (next: HSV) => {
        hsvSet(next)
        textSet(toHex(next))
    }

    /** Follows the pointer across the whole window so the handle doesn't drop when it leaves the box. */
    const track = (e: React.PointerEvent<HTMLDivElement>, onMove: (x: number, y: number) => void) => {
        e.preventDefault()
        const rect = e.currentTarget.getBoundingClientRect()

        const apply = (cx: number, cy: number) => onMove(
            Math.min(1, Math.max(0, (cx - rect.left) / rect.width)),
            Math.min(1, Math.max(0, (cy - rect.top) / rect.height))
        )

        apply(e.clientX, e.clientY)

        const move = (ev: PointerEvent) => apply(ev.clientX, ev.clientY)
        const up = () => {
            window.removeEventListener("pointermove", move)
            window.removeEventListener("pointerup", up)
        }
        window.addEventListener("pointermove", move)
        window.addEventListener("pointerup", up)
    }

    return (
        <div className="color-pad" onClick={e => e.stopPropagation()}>

            <div className="color-pad-area"
                style={{
                    backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
                    backgroundImage: "linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, rgba(255,255,255,0))"
                }}
                onPointerDown={e => track(e, (x, y) => update({ ...hsv, s: x * 100, v: (1 - y) * 100 }))}>
                <span className="color-pad-handle"
                    style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%`, backgroundColor: hex }} />
            </div>

            <div className="color-pad-hue"
                onPointerDown={e => track(e, x => update({ ...hsv, h: x * 360 }))}>
                <span className="color-pad-handle"
                    style={{ left: `${(hsv.h / 360) * 100}%`, top: "50%", backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }} />
            </div>

            <div className="color-pad-row">
                <span className="color-pad-preview" style={{ backgroundColor: hex }} />
                <input type="text" value={text} maxLength={7} spellCheck={false}
                    onChange={e => {
                        textSet(e.target.value.toUpperCase())
                        const valid = normalizeHex(e.target.value)
                        if (valid) {
                            const o = Color(valid).hsv().object()
                            hsvSet({ h: o.h ?? 0, s: o.s ?? 0, v: o.v ?? 0 })
                        }
                    }} />
            </div>

            <div className="color-pad-actions">
                <button type="button" className="color-pad-btn"
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        props.onCancel()
                    }}>
                    Cancel
                </button>
                <button type="button" className="color-pad-btn is-primary"
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        props.onUse(hex)
                    }}>
                    Use Color
                </button>
            </div>

        </div>
    )
}
